import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { authApi } from '@/auth/authApi';
import { useAuthStore } from '@/store/authStore';
import { paths } from '@/routes/paths';
import { applyServerErrors } from '@/utils/formErrors';
import { loginSchema, mfaSchema, type LoginValues, type MfaValues } from '@/features/auth/schemas';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get('redirect') ?? paths.dashboard;
  const expired = params.get('expired') === '1';

  const [formError, setFormError] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const credentialsForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });
  const mfaForm = useForm<MfaValues>({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: '' },
  });

  const loginMutation = useMutation({
    mutationFn: (values: LoginValues) => authApi.login(values),
    onSuccess: (result) => {
      setFormError('');
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        return;
      }
      useAuthStore.getState().setSession({
        accessToken: result.accessToken,
        expiresIn: result.expiresIn,
        user: result.user,
      });
      navigate(redirectTo, { replace: true });
    },
    onError: (error) =>
      setFormError(applyServerErrors(error, credentialsForm.setError, ['email', 'password'])),
  });

  const mfaMutation = useMutation({
    mutationFn: (values: MfaValues) => authApi.loginMfa({ mfaToken: mfaToken!, code: values.code }),
    onSuccess: (result) => {
      if (result.mfaRequired) return; // shouldn't happen at this step
      useAuthStore.getState().setSession({
        accessToken: result.accessToken,
        expiresIn: result.expiresIn,
        user: result.user,
      });
      navigate(redirectTo, { replace: true });
    },
    onError: (error) => setFormError(applyServerErrors(error, mfaForm.setError, ['code'])),
  });

  if (mfaToken) {
    return (
      <Stack
        spacing={2.5}
        component="form"
        onSubmit={mfaForm.handleSubmit((v) => mfaMutation.mutate(v))}
      >
        <Box>
          <Typography variant="h6">Two-factor verification</Typography>
          <Typography variant="body2" color="text.secondary">
            Enter the 6-digit code from your authenticator app, or a backup code.
          </Typography>
        </Box>
        {formError && <Alert severity="error">{formError}</Alert>}
        <TextField
          label="Authentication code"
          autoFocus
          autoComplete="one-time-code"
          inputProps={{ inputMode: 'numeric' }}
          error={!!mfaForm.formState.errors.code}
          helperText={mfaForm.formState.errors.code?.message}
          {...mfaForm.register('code')}
        />
        <Button type="submit" variant="contained" size="large" disabled={mfaMutation.isPending}>
          {mfaMutation.isPending ? 'Verifying…' : 'Verify & sign in'}
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setMfaToken(null);
            setFormError('');
          }}
        >
          Back to sign in
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2.5}
      component="form"
      onSubmit={credentialsForm.handleSubmit((v) => loginMutation.mutate(v))}
    >
      <Box>
        <Typography variant="h6">Sign in</Typography>
        <Typography variant="body2" color="text.secondary">
          Access your agency admin workspace.
        </Typography>
      </Box>

      {expired && !formError && (
        <Alert severity="warning">Your session expired. Please sign in again.</Alert>
      )}
      {formError && <Alert severity="error">{formError}</Alert>}

      <TextField
        label="Email"
        type="email"
        autoComplete="email"
        autoFocus
        error={!!credentialsForm.formState.errors.email}
        helperText={credentialsForm.formState.errors.email?.message}
        {...credentialsForm.register('email')}
      />
      <TextField
        label="Password"
        type="password"
        autoComplete="current-password"
        error={!!credentialsForm.formState.errors.password}
        helperText={credentialsForm.formState.errors.password?.message}
        {...credentialsForm.register('password')}
      />

      <Stack direction="row" justifyContent="flex-end">
        <Link component={RouterLink} to={paths.forgotPassword} variant="body2" underline="hover">
          Forgot password?
        </Link>
      </Stack>

      <Button type="submit" variant="contained" size="large" disabled={loginMutation.isPending}>
        {loginMutation.isPending ? 'Signing in…' : 'Sign in'}
      </Button>
    </Stack>
  );
}
